import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtractionHistoryItem, ExtractionStatus } from '../types/wiseboard';
import ExtractionDetailPanel from './ExtractionDetailPanel';

interface Props {
  item: ExtractionHistoryItem;
  onRetry?: (id: string) => void;
  onViewFile?: (storagePath: string) => void;
}

const statusConfig: Record<ExtractionStatus, { label: string; className: string; icon?: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
  },
  extracting: {
    label: 'Extracting',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    icon: <Loader2 className="h-3 w-3 animate-spin" />
  },
  extracted: {
    label: 'Extracted',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
  },
  classifying: {
    label: 'Classifying',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    icon: <Loader2 className="h-3 w-3 animate-spin" />
  },
  classified: {
    label: 'Classified',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
  },
  splitting: {
    label: 'Splitting',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    icon: <Loader2 className="h-3 w-3 animate-spin" />
  },
  completed: {
    label: 'Complete',
    className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    icon: <AlertCircle className="h-3 w-3" />
  }
};

function formatDuration(ms: number | null): string {
  if (ms === null) {
return '—';
}
  if (ms < 1000) {
return `${ms}ms`;
}
  const seconds = ms / 1000;
  return `${seconds.toFixed(1)}s`;
}

function getDocumentName(storagePath: string, title: string | null): string {
  if (title) {
return title;
}
  // Extract filename from path
  const parts = storagePath.split('/');
  const filename = parts[parts.length - 1];
  // Truncate UUID prefix if present
  if (filename.length > 40) {
    return `${filename.slice(0, 8)}...${filename.slice(-15)}`;
  }
  return filename;
}

export function ExtractionHistoryRow({ item, onRetry, onViewFile }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = statusConfig[item.status];

  const displayName = getDocumentName(item.storage_path, item.document_title);
  const loanDisplay = item.loan_number || item.borrower_name || item.encompass_loan_id.slice(0, 8);

  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors',
          isExpanded && 'bg-muted/30'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Expand Toggle */}
        <TableCell className="w-10 p-2">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>

        {/* Document */}
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="truncate max-w-[200px]" title={item.document_title || item.storage_path}>
              {displayName}
            </span>
          </div>
        </TableCell>

        {/* Loan */}
        <TableCell>
          <span className="text-sm" title={item.encompass_loan_id}>
            {loanDisplay}
          </span>
        </TableCell>

        {/* Status */}
        <TableCell>
          <Badge variant="secondary" className={cn('gap-1', config.className)}>
            {config.icon}
            {config.label}
          </Badge>
        </TableCell>

        {/* GPU */}
        <TableCell className="text-center">
          <span className="text-sm text-muted-foreground">
            {item.processed_by_gpu?.toUpperCase() || '—'}
          </span>
        </TableCell>

        {/* Pages */}
        <TableCell className="text-center">
          <span className="font-medium text-blue-600">
            {item.total_pages ?? '—'}
          </span>
        </TableCell>

        {/* Time */}
        <TableCell className="text-center">
          <span className="font-medium text-amber-600">
            {formatDuration(item.extraction_time_ms)}
          </span>
        </TableCell>

        {/* Created */}
        <TableCell className="text-sm text-muted-foreground">
          {format(parseISO(item.created_at), 'MMM d, h:mm a')}
        </TableCell>
      </TableRow>

      {/* Expanded Detail Row */}
      {isExpanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={8} className="p-0">
            <ExtractionDetailPanel
              item={item}
              onRetry={onRetry}
              onViewFile={onViewFile}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default ExtractionHistoryRow;
